import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/outline';
import { toast } from 'react-toastify';
import DeleteConfirmationModal from '../../components/common/DeleteConfirmationModal';
import EmployeeForm from '../../components/users/EmployeeForm';
import { userService } from '../../services/user';

const EmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeToDeactivate, setEmployeeToDeactivate] = useState(null);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setEmployees(await userService.getEmployees());
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.detail || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const saveEmployee = async (formData) => {
    try {
      setSaving(true);
      const payload = {
        ...formData,
        team_id: formData.team_id ? Number(formData.team_id) : null
      };
      if (selectedEmployee) {
        await userService.updateEmployee(selectedEmployee.id, payload);
        toast.success('Employee updated');
      } else {
        await userService.createEmployee(payload);
        toast.success('Employee created. Login credentials will be emailed.');
      }
      setFormOpen(false);
      setSelectedEmployee(null);
      await fetchEmployees();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.detail || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const deactivateEmployee = async () => {
    if (!employeeToDeactivate) return;
    try {
      await userService.deactivateEmployee(employeeToDeactivate.id);
      toast.success('Employee deactivated');
      setEmployeeToDeactivate(null);
      await fetchEmployees();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.detail || 'Failed to deactivate employee');
    }
  };

  return (
    <div className="min-h-screen px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Employees</h1>
            <p className="mt-1 text-sm text-gray-500">Manage employees who can submit internal support tickets.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setSelectedEmployee(null);
              setFormOpen(true);
            }}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Add Employee
          </button>
        </div>

        {formOpen && (
          <EmployeeForm
            employee={selectedEmployee}
            onClose={() => {
              setFormOpen(false);
              setSelectedEmployee(null);
            }}
            onSubmit={saveEmployee}
            isSubmitting={saving}
          />
        )}

        <DeleteConfirmationModal
          isOpen={Boolean(employeeToDeactivate)}
          onClose={() => setEmployeeToDeactivate(null)}
          onConfirm={deactivateEmployee}
          title="Deactivate Employee"
          message={`Are you sure you want to deactivate ${employeeToDeactivate?.name}?`}
        />

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Loading employees...</div>
          ) : employees.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">No employees have been added yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Team</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {employees.map((employee) => (
                    <tr key={employee.id}>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{employee.name}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">{employee.email}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">{employee.team?.name || '—'}</td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${employee.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {employee.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right">
                        <button
                          type="button"
                          title="Edit employee"
                          onClick={() => {
                            setSelectedEmployee(employee);
                            setFormOpen(true);
                          }}
                          className="mr-3 text-indigo-600 hover:text-indigo-900"
                        >
                          <PencilIcon className="inline h-5 w-5" />
                        </button>
                        {employee.is_active && (
                          <button
                            type="button"
                            title="Deactivate employee"
                            onClick={() => setEmployeeToDeactivate(employee)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="inline h-5 w-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeesPage;
